import { BarChart, EmojiEvents, RocketLaunch } from '@mui/icons-material'
import { Box, Card, CardContent, Skeleton } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FeaturePromoBanner } from '../../../../components'
import { ACCELERATE_STATUS } from '../../../../consts'
import { accelerateSlice } from '../../../../reducers'
import { routes } from '../../../../router/routes'

const useStyles = makeStyles(({ spacing, palette, breakpoints }) => ({
  skeletonRoot: {
    width: '100%',
    marginTop: spacing(4),
    borderRadius: spacing(2),
    background: `linear-gradient(135deg, ${palette.common.white} 0%, ${palette.primary.shade[5]} 50%, ${palette.primary.shade[10]} 100%)`,
    border: `1px solid ${palette.primary.shade[20]}`
  },
  skeletonContent: {
    padding: spacing(4),
    display: 'flex',
    gap: spacing(4),
    [breakpoints.down('md')]: {
      flexDirection: 'column',
      padding: spacing(3)
    }
  },
  skeletonLeft: {
    flex: 7,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2),
    [breakpoints.down('md')]: {
      flex: 'unset'
    }
  },
  skeletonRight: {
    flex: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(3),
    [breakpoints.down('md')]: {
      flex: 'unset'
    }
  },
  skeletonTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2)
  },
  skeletonFeatureRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2)
  }
}))

// Icon mapping for programIcon
const PROGRAM_ICONS = {
  RocketLaunch,
  BarChart,
  EmojiEvents
}

/**
 * Banner configurations for each accelerate journey state
 */
const BANNER_CONFIGS = {
  // State 1: Not Started - User hasn't begun the journey
  [ACCELERATE_STATUS.ASSESSMENT_PENDING]: {
    badgeText: null,
    programIcon: 'RocketLaunch',
    title: 'Skills Accelerator',
    subtitle: 'Your Management Journey',
    description: 'Get tailored insights into your management style and a clear path to grow. This assessment identifies your strengths and development areas, then builds a personalized learning roadmap aligned to your goals.',
    ctaLabel: 'Start Your Journey',
    ctaRoute: routes.accelerate.path,
    features: [
      { title: 'Assess Your Skills', description: 'Evaluate your current abilities', status: 'pending' },
      { title: 'Build Your Skills', description: 'Learn and develop new skills', status: 'pending' },
      { title: 'Apply Your Skills', description: 'Practice and apply them in real-world scenarios', status: 'pending' }
    ]
  },

  // State 2: Assessment In Progress - User started but hasn't completed the assessment
  [ACCELERATE_STATUS.ASSESSMENT_IN_PROGRESS]: {
    badgeText: 'IN PROGRESS',
    programIcon: 'BarChart',
    title: 'Complete Your Assessment',
    subtitle: 'Resume Where You Left Off',
    description: "You're on your way! Complete your Manager Skills Assessment to unlock personalized insights about your management style. It only takes 15 minutes, and you can pick up right where you left off.",
    ctaLabel: 'Resume Assessment',
    ctaRoute: routes.accelerate.path,
    features: [
      { title: 'Assess Your Skills', description: 'Complete your Manager Skills Assessment', status: 'current' },
      { title: 'Build Your Skills', description: 'Personalized learning paths await', status: 'pending' },
      { title: 'Apply Your Skills', description: 'Practice in real-world scenarios', status: 'pending' }
    ]
  },

  // State 3: Report Ready - Assessment complete, report available to view
  [ACCELERATE_STATUS.ASSESSMENT_COMPLETED]: {
    badgeText: 'REPORT READY',
    programIcon: 'BarChart',
    title: 'Your Results Are Ready!',
    subtitle: 'Discover Your Management Profile',
    description: 'Great work completing your assessment! Your personalized Manager Skills Report is ready. Review your strengths, explore development areas, and get your custom learning roadmap to accelerate your management journey.',
    ctaLabel: 'View Your Report',
    ctaRoute: routes['accelerate-assessment-report'].path,
    features: [
      { title: 'Assess Your Skills', description: 'Assessment completed', status: 'completed' },
      { title: 'Build Your Skills', description: 'Start your personalized learning journey', status: 'current' },
      { title: 'Apply Your Skills', description: 'Practice in real-world scenarios', status: 'pending' }
    ]
  },

  // State 4a: Report Viewed - User has seen report, ready to build skills
  [ACCELERATE_STATUS.REPORT_VIEWED]: {
    badgeText: 'LEARNING IN PROGRESS',
    programIcon: 'BarChart',
    title: 'Keep Building Your Skills',
    subtitle: "You're Making Great Progress",
    description: "You're on track with your personalized learning journey! Continue developing your management skills with curated content designed to help you grow. Your consistent effort is building stronger management capabilities.",
    ctaLabel: 'Build Your Skills',
    ctaRoute: routes['learning-journey'].path,
    features: [
      { title: 'Assess Your Skills', description: 'Assessment completed', status: 'completed' },
      { title: 'Build Your Skills', description: 'Continue your learning journey', status: 'current' },
      { title: 'Apply Your Skills', description: 'Practice in real-world scenarios', status: 'pending' }
    ]
  },

  // State 4b: Skill Builder In Progress - User is actively working on learning journey
  [ACCELERATE_STATUS.SKILL_BUILDER_IN_PROGRESS]: {
    badgeText: 'LEARNING IN PROGRESS',
    programIcon: 'BarChart',
    title: 'Keep Building Your Skills',
    subtitle: "You're Making Great Progress",
    description: "You're on track with your personalized learning journey! Continue developing your management skills with curated content designed to help you grow. Your consistent effort is building stronger management capabilities.",
    ctaLabel: 'Build Your Skills',
    ctaRoute: routes['learning-journey'].path,
    features: [
      { title: 'Assess Your Skills', description: 'Assessment completed', status: 'completed' },
      { title: 'Build Your Skills', description: 'Continue your learning journey', status: 'current' },
      { title: 'Apply Your Skills', description: 'Practice in real-world scenarios', status: 'pending' }
    ]
  },

  // State 5: Journey Complete - All phases completed (100% progress)
  [ACCELERATE_STATUS.SKILL_BUILDER_COMPLETED]: {
    badgeText: 'CONGRATULATIONS',
    programIcon: 'EmojiEvents',
    title: 'Journey Complete!',
    subtitle: "You've Mastered Your Skills",
    description: "Incredible work! You've completed your Skills Accelerator journey and developed essential management capabilities. Review your progress, celebrate your achievements, and consider retaking the assessment to measure your growth.",
    ctaLabel: 'Apply Your Skills',
    ctaRoute: routes.accelerate.path,
    features: [
      { title: 'Skills Mastered', description: 'Review all completed skills', status: 'completed' },
      { title: 'Achievements Earned', description: 'Celebrate your milestones', status: 'completed' },
      { title: 'Measure Your Growth', description: 'Retake assessment to see improvement', status: 'completed' }
    ]
  }
}

// Default config for NOT_ENABLED or unknown states (same as ASSESSMENT_PENDING)
const DEFAULT_CONFIG = BANNER_CONFIGS[ACCELERATE_STATUS.ASSESSMENT_PENDING]

/**
 * Skeleton loader for the accelerate banner while data is loading
 */
const AccelerateBannerSkeleton = () => {
  const classes = useStyles()

  return (
    <Card className={classes.skeletonRoot} elevation={0} data-test='home-accelerate-banner-skeleton'>
      <CardContent className={classes.skeletonContent}>
        <Box className={classes.skeletonLeft}>
          <Box className={classes.skeletonTitleRow}>
            <Skeleton variant='rounded' width={56} height={56} />
            <Box>
              <Skeleton variant='text' width={180} height={32} />
              <Skeleton variant='text' width={140} height={20} />
            </Box>
          </Box>
          <Skeleton variant='text' width='100%' height={20} />
          <Skeleton variant='text' width='90%' height={20} />
          <Skeleton variant='text' width='70%' height={20} />
          <Skeleton variant='rounded' width={160} height={44} sx={{ mt: 1 }} />
        </Box>
        <Box className={classes.skeletonRight}>
          {[1, 2, 3].map((i) => (
            <Box key={i} className={classes.skeletonFeatureRow}>
              <Skeleton variant='circular' width={28} height={28} />
              <Box flex={1}>
                <Skeleton variant='text' width='60%' height={22} />
                <Skeleton variant='text' width='80%' height={18} />
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  )
}

export const AccelerateBanner = () => {
  const navigate = useNavigate()
  const status = useSelector(accelerateSlice.selectors.selectStatus)
  const isEnabled = useSelector(accelerateSlice.selectors.selectIsEnabled)
  const isLoading = useSelector(accelerateSlice.selectors.selectIsLoading)

  // Show skeleton while loading
  if (isLoading) {
    return <AccelerateBannerSkeleton />
  }

  // Don't render banner if accelerate is not enabled for this user/tenant
  if (!isEnabled) {
    return null
  }

  // Get the appropriate banner config based on status
  const bannerConfig = BANNER_CONFIGS[status] || DEFAULT_CONFIG

  // Map programIcon string to component
  const programIcon = bannerConfig.programIcon ? PROGRAM_ICONS[bannerConfig.programIcon] : null

  const handleCtaClick = () => {
    const state = status === ACCELERATE_STATUS.ASSESSMENT_PENDING ? { openOnboarding: true } : {}
    navigate(bannerConfig.ctaRoute, { state })
  }

  return (
    <FeaturePromoBanner
      badgeText={bannerConfig.badgeText}
      programIcon={programIcon}
      title={bannerConfig.title}
      subtitle={bannerConfig.subtitle}
      description={bannerConfig.description}
      buttonLabel={bannerConfig.ctaLabel}
      buttonOnClick={handleCtaClick}
      features={bannerConfig.features}
      useBrandBorder
      dataTest='home-accelerate-banner'
    />
  )
}
